import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { IFullAppointmentType } from 'model/v2';
import FormItem from 'views/containers/form/formItem/formItem';
import { Select } from 'api/sharedComponents/reactHookFormComponents';
import { useSfrReasons } from 'hooks/SfrReasonsHook';
import { SfrReasonFormWrapper } from './style';

interface Props {
  apptTypes?: IFullAppointmentType[];
}

/**
 * Special Funding Request reason (AXON-3602).
 *
 * Shown only for appointment types flagged `requiresSfrReason` — School Special
 * Funding and its variants — because school districts reimburse those sessions
 * outside the standard therapy rate and billing needs to know why each was
 * requested. Always optional: most appointments have no SFR reason.
 *
 * Gated on the flag rather than a per-environment appointment-type id list, so
 * onboarding another Special Funding type needs no frontend release.
 */
const SfrReasonForm: React.FC<Props> = ({ apptTypes }: Props) => {
  const methods = useFormContext();
  const { sfrReasons } = useSfrReasons();

  const appType = useWatch({ control: methods.control, name: 'appType' });
  const appSubType = useWatch({ control: methods.control, name: 'appSubType' });

  const requiresSfrReason = React.useMemo(() => {
    if (!apptTypes?.length) return false;

    const parent = apptTypes.find(type => type.id === appType);
    if (parent?.requiresSfrReason) return true;

    return !!parent?.appointmentSubTypes?.find(
      subType => subType.id === appSubType
    )?.requiresSfrReason;
  }, [apptTypes, appType, appSubType]);

  // Clear a stale reason if the user switches to a type that doesn't take one,
  // so a hidden field can't submit a value the appointment no longer supports.
  React.useEffect(() => {
    if (!requiresSfrReason && methods.getValues('sfrReasonId')) {
      methods.setValue('sfrReasonId', null);
    }
  }, [requiresSfrReason, methods]);

  if (!requiresSfrReason) return null;

  return (
    <SfrReasonFormWrapper>
      <FormItem optional label="SFR Reason">
        <Controller
          name="sfrReasonId"
          control={methods.control}
          render={fieldProps => (
            <Select
              className="appt-category-type"
              allowClear
              placeholder="Select SFR Reason"
              options={sfrReasons}
              field={fieldProps}
              errors={methods.errors}
            />
          )}
        />
      </FormItem>
    </SfrReasonFormWrapper>
  );
};

export default React.memo(SfrReasonForm);
